export const CURRENT_SCHEMA_VERSION = 2 as const;

export type StudyRecordType =
  | 'profile'
  | 'study-plan'
  | 'assignment'
  | 'study-block'
  | 'study-session'
  | 'task-completion'
  | 'practice-attempt'
  | 'daily-report'
  | 'mistake'
  | 'review-event'
  | 'oral-quiz'
  | 'case-submission'
  | 'coach-feedback'
  | 'activity-event'
  | 'plan-revision'
  | 'plan-day';

export type RecordSource = 'app' | 'coach' | 'legacy-localStorage' | 'import' | 'remote';

export interface StudyRecord<TPayload extends Record<string, unknown> = Record<string, unknown>> {
  /** Stable IndexedDB key. Always `${type}:${id}`. */
  key: string;
  id: string;
  type: StudyRecordType;
  schemaVersion: number;
  revision: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  deviceId: string;
  source: RecordSource;
  studyDate?: string;
  payload: TPayload;
}

export type AttachmentOwnerType =
  | 'daily-report'
  | 'mistake'
  | 'case-submission'
  | 'study-session'
  | 'oral-quiz';

export interface AttachmentMetadata {
  id: string;
  ownerType: AttachmentOwnerType;
  ownerId: string;
  ownerKey: string;
  schemaVersion: number;
  fileName: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
  width?: number;
  height?: number;
  capturedAt?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  exifStripped: boolean;
  deviceId: string;
  revision: number;
  remoteObjectKey?: string;
}

export interface StoredAttachment extends AttachmentMetadata {
  blob: Blob;
}

export interface SettingRecord<T = unknown> {
  key: string;
  value: T;
  updatedAt: string;
}

export type OutboxOperation = 'record-upsert' | 'record-delete' | 'attachment-upsert' | 'attachment-delete';

export interface OutboxEntry {
  id: string;
  operation: OutboxOperation;
  entityKey: string;
  /** 本次离线编辑开始前最后确认的云端 revision；连续本机保存不得推进它。 */
  baseRevision: number;
  payload: unknown;
  createdAt: string;
  attempts: number;
  lastError?: string;
}

export interface SnapshotRecord {
  id: string;
  reason: 'before-import' | 'before-migration' | 'before-clear' | 'manual';
  createdAt: string;
  schemaVersion: number;
  blob: Blob;
}

export interface DatabaseDump {
  records: StudyRecord[];
  attachments: StoredAttachment[];
  settings: SettingRecord[];
  outbox: OutboxEntry[];
}

export interface PortableAttachment extends AttachmentMetadata {
  dataBase64?: string;
  path?: string;
}

export interface PortableDump {
  records: StudyRecord[];
  attachments: PortableAttachment[];
  settings: SettingRecord[];
  outbox: OutboxEntry[];
}

export interface BackupManifest {
  format: 'nse-study-bundle';
  schemaVersion: number;
  appVersion: string;
  exportId: string;
  exportedAt: string;
  deviceId: string;
  contentVersion?: string;
  kind: 'json' | 'zip';
  counts: {
    records: number;
    attachments: number;
    settings: number;
    outbox: number;
  };
  integrity: {
    algorithm: 'SHA-256';
    dataHash: string;
  };
  files?: Array<{ path: string; sha256: string; byteSize: number }>;
}

export interface JsonBackupEnvelope {
  manifest: BackupManifest;
  data: PortableDump;
}

export type RestoreMode = 'merge' | 'replace';

export interface RestoreResult {
  mode: RestoreMode;
  records: number;
  attachments: number;
  settings: number;
  outbox: number;
  warnings: string[];
}

export interface SyncPullResult {
  records: StudyRecord[];
  cursor?: string;
}

export interface SyncedAttachment {
  metadata: AttachmentMetadata;
  blob?: Blob;
}

export interface AttachmentSyncPullResult {
  attachments: SyncedAttachment[];
  cursor?: string;
  failures?: SyncPullFailure[];
}

export interface SyncPushResult {
  outcomes: SyncPushOutcome[];
}

export interface SyncPushAccepted {
  status: 'accepted';
  opId: string;
  entityKey: string;
  remoteRevision: number;
  remote?: unknown;
}

export interface SyncPushConflict {
  status: 'conflict';
  opId: string;
  entityKey: string;
  localRevision: number;
  remoteRevision?: number;
  remote?: unknown;
}

export interface SyncPushFailure {
  status: 'failed';
  opId: string;
  entityKey: string;
  message: string;
  code?: string;
  retryable: boolean;
}

export type SyncPushOutcome = SyncPushAccepted | SyncPushConflict | SyncPushFailure;

export interface SyncPullFailure {
  entityKey: string;
  message: string;
  remoteRevision?: number;
}

export interface SyncRunResult {
  mode: 'offline' | 'synced' | 'partial';
  pushed: number;
  pulled: number;
  conflicts: number;
  pending: number;
  cursor?: string;
  errors: string[];
}

export const recordKey = (type: StudyRecordType, id: string) => `${type}:${id}`;
